/**
 * Pure helpers behind the price chart's lines: the 1h EMAs the engine trades
 * on, placed on any timeframe, the RSI and volume yardsticks its checklist
 * grades, and merging live candle updates.
 */

export interface Candle { time: number; open: number; high: number; low: number; close: number; volume: number }

/**
 * Wilder's RSI exactly as the engine computes it (backend
 * services/indicators.ts calculateRSI): the first averages are plain means
 * of the first `period` changes, then smoothed; a run with no losses reads
 * 99.01, not 100, because the engine caps the ratio at 100. Null until the
 * seed exists, so the line never starts on a half-formed value.
 */
export function rsiSeries(values: number[], period = 14): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null);
  if (values.length < period + 1) return out;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const change = values[i]! - values[i - 1]!;
    if (change > 0) gains += change; else losses -= change;
  }
  let avgGain = gains / period, avgLoss = losses / period;
  const rsi = () => {
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    return 100 - 100 / (1 + rs);
  };
  out[period] = rsi();
  for (let i = period + 1; i < values.length; i++) {
    const change = values[i]! - values[i - 1]!;
    avgGain = (avgGain * (period - 1) + (change > 0 ? change : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (change < 0 ? -change : 0)) / period;
    out[i] = rsi();
  }
  return out;
}

/**
 * The engine's volume yardstick (setupClassifier avgVolume): for each bar,
 * the mean volume of the `n` bars BEFORE it, so a bar's own volume divided
 * by it is the "× avg" the trigger rule grades (≥ 0.5× to count, ≥ 1.2× for
 * A+). Null until n earlier bars exist.
 */
export function priorVolumeAverage(volumes: number[], n = 20): (number | null)[] {
  const out: (number | null)[] = new Array(volumes.length).fill(null);
  let sum = 0;
  for (let i = 0; i < volumes.length; i++) {
    if (i >= n) out[i] = sum / n;
    sum += volumes[i]!;
    if (i >= n) sum -= volumes[i - n]!;
  }
  return out;
}

/**
 * EMA seeded with the average of the first `period` values, the way the
 * engine computes it (backend signals/advancedIndicators.ts). Null until the
 * seed exists, so the line never starts on a half-formed value.
 */
export function emaSeries(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null);
  if (values.length < period) return out;
  const k = 2 / (period + 1);
  let ema = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
  out[period - 1] = ema;
  for (let i = period; i < values.length; i++) {
    ema = values[i]! * k + ema * (1 - k);
    out[i] = ema;
  }
  return out;
}

/** Below this share of the bar the volume projection is too noisy to scale further (backend closePreview.ts). */
const MIN_VOLUME_FRACTION = 1 / 12;

/**
 * The volume a forming bar is on pace for over its full span: the desk's
 * "volume so far is on pace for 0.5× a normal hour" (backend closePreview.ts
 * projects the same way). A bar that has already closed is returned as is.
 */
export function pacedVolume(volume: number, barOpenSec: number, tfSec: number, nowSec: number): number {
  const fraction = Math.min(1, Math.max(MIN_VOLUME_FRACTION, (nowSec - barOpenSec) / tfSec));
  return volume / fraction;
}

/**
 * Higher-timeframe values on chart bars: each bar gets the value of the last
 * HTF bar that had CLOSED by the bar's open, i.e. what the engine knew at that
 * moment. No repainting: the line steps once per HTF close. Times in seconds,
 * ascending; `htfTimes` are bar open times.
 */
export function stepToBars(barTimes: number[], htfTimes: number[], htfValues: (number | null)[], htfSec: number): (number | null)[] {
  const out: (number | null)[] = [];
  let j = -1;
  for (const t of barTimes) {
    while (j + 1 < htfTimes.length && htfTimes[j + 1]! + htfSec <= t) j++;
    out.push(j >= 0 ? htfValues[j] ?? null : null);
  }
  return out;
}

/**
 * A fresh candle list against the one on the chart: the bars to send to the
 * chart (the forming bar onward) and whether a new bar opened. A stale reply
 * (older than the chart) changes nothing.
 */
export function freshBars(current: Candle[], fresh: Candle[]): { updates: Candle[]; newBar: boolean } {
  const last = current[current.length - 1];
  if (!last) return { updates: fresh, newBar: fresh.length > 0 };
  const updates = fresh.filter(c => c.time >= last.time);
  return { updates, newBar: updates.some(c => c.time > last.time) };
}
